import React, { useState, useMemo, useEffect } from 'react';
import useSWR from 'swr';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
 Text,
 Flex,
 Box,
 Tabs,
 TabList,
 TabPanels,
 Tab,
 TabPanel,
 Breadcrumb,
 BreadcrumbItem,
 BreadcrumbLink,
} from '@chakra-ui/react';
import { IoChevronForwardSharp } from 'react-icons/io5';
import Questionnaire from './Questionnaire';
import { fetchData } from '../../services/fetch';
import { CompanyHeader } from '../../components/Card';
import MainContent from '../../components/Layout/MainContent';
import Loader from '../../components/Feedback/Loader';
import PeriodicType from './PeriodicType';
import DiagnosisType from './DiagnosisType';
import { Select } from '../../components/Input';
import CertificationContent from './certifications/CertificationContent';
import PillarCertification from './PillarCertification';
import SimpleCertification from './SimpleCertification';

export default function Questionnaires() {
 const { id: idCompany } = useParams();
 const [selectedCompanyId, setSelectedCompanyId] = useState(idCompany);
 const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(null);
 const [selectedCertificationId, setSelectedCertificationId] = useState(null);
 const [activeTab, setActiveTab] = useState(0);
 const [openChat, setOpenChat] = useState(null);
 const [filterStatus, setFilterStatus] = useState('true');
 const [filterExercise, setFilterExercise] = useState('');
 const [filterCertExercise, setFilterCertExercise] = useState('');
 const [filterCertStatus, setFilterCertStatus] = useState('');
 const [availableExercises, setAvailableExercises] = useState([]);
 const [availableCertExercises, setAvailableCertExercises] = useState([]);

 const location = useLocation();
 const urlParams = new URLSearchParams(location.search);

 const {
  data: companyData,
  error: companyError,
  isLoading: companyLoading,
 } = useSWR(idCompany ? `/company/${idCompany}` : null, fetchData);

 const formsQueryParams = useMemo(() => {
  const params = new URLSearchParams();
  if (filterStatus !== '') params.set('status', filterStatus);
  if (filterExercise !== '') params.set('exercise', filterExercise);
  return params.toString();
 }, [filterStatus, filterExercise]);

 const {
  data: formsList,
  error: formsError,
  isLoading: formsLoading,
 } = useSWR(
  idCompany ? `/company/linkSurveyCompany/${idCompany}?${formsQueryParams}` : null,
  fetchData
 );

 const certQueryParams = useMemo(() => {
  const params = new URLSearchParams();
  if (filterCertExercise !== '') params.set('exercise', filterCertExercise);
  if (filterCertStatus !== '') params.set('status', filterCertStatus);
  return params.toString();
 }, [filterCertExercise, filterCertStatus]);

 const { data: certificationsList } = useSWR(
  companyData?.idCompanyGroup
   ? `certificate/link-group/${companyData.idCompanyGroup}${certQueryParams ? `?${certQueryParams}` : ''}`
   : null,
  fetchData
 );

 const { data: allCertificationsList } = useSWR(
  companyData?.idCompanyGroup
   ? `certificate/link-group/${companyData.idCompanyGroup}`
   : null,
  fetchData
 );

 useEffect(() => {
  if (Array.isArray(formsList) && formsList.length > 0 && !selectedQuestionnaireId) {
   setSelectedQuestionnaireId(formsList[0].s_id_survey);
  }
 }, [formsList, selectedQuestionnaireId]);

 // Reset available exercises when company changes
 useEffect(() => {
  setAvailableExercises([]);
  setAvailableCertExercises([]);
 }, [idCompany]);

 // Populate available exercises only on first load of each company
 useEffect(() => {
  if (Array.isArray(formsList) && availableExercises.length === 0) {
   const years = [...new Set(formsList.map((f) => f.s_exercise).filter(Boolean))].sort();
   setAvailableExercises(years);
  }
 }, [formsList, availableExercises.length]);

 // Populate cert exercises from certifications list (all, without filters)
 useEffect(() => {
  if (Array.isArray(allCertificationsList) && availableCertExercises.length === 0) {
   const years = [...new Set(allCertificationsList.map((c) => c.exercise).filter(Boolean))].sort();
   setAvailableCertExercises(years);
  }
 }, [allCertificationsList, availableCertExercises.length]);

 useEffect(() => {
  if (Array.isArray(certificationsList) && certificationsList.length > 0 && !selectedCertificationId) {
   setSelectedCertificationId(certificationsList[0].id_certificate);
  }
 }, [certificationsList, selectedCertificationId]);

 useEffect(() => {
  if (Array.isArray(formsList)) {
   verifyRedirectFromEmail();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [formsList]);

 const deleteRedirectEmailParams = () => {
  urlParams.delete('surveyId');
  urlParams.delete('questionId');
  urlParams.delete('themeId');

  window.history.replaceState(
   {},
   '',
   `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`
  );
 };

 const verifyRedirectFromEmail = () => {
  const surveyId = parseInt(urlParams.get('surveyId'), 10) || null;
  const questionId = parseInt(urlParams.get('questionId'), 10) || null;
  const themeId = parseInt(urlParams.get('themeId'), 10) || null;

  deleteRedirectEmailParams();

  if (surveyId && questionId && Array.isArray(formsList)) {
   handleQuestionnaireChange({ target: { value: surveyId } });
   setOpenChat({ surveyId, questionId, themeId });
  }
 };

 const questionnaireOptions = useMemo(() => {
  if (!Array.isArray(formsList) || !formsList.length) return [];

  return formsList.map((form) => ({
   value: form.s_id_survey,
   label: form.s_description,
  }));
 }, [formsList]);

 const certificationOptions = useMemo(() => {
  if (!Array.isArray(certificationsList) || !certificationsList.length) return [];

  return certificationsList.map((certification) => ({
   value: certification.id_certificate,
   label: certification.name,
  }));
 }, [certificationsList]);

 const handleQuestionnaireChange = (e) => {
  if (openChat) {
   setOpenChat(null);
  }
  setSelectedQuestionnaireId(Number(e.target.value));
 };

 const handleCertificationChange = (e) => {
  setSelectedCertificationId(Number(e.target.value));
 };

 const selectedQuestionnaire = useMemo(
  () => Array.isArray(formsList) ? formsList.find((form) => form.s_id_survey === selectedQuestionnaireId) : undefined,
  [formsList, selectedQuestionnaireId]
 );

 const selectedCertification = useMemo(
  () => Array.isArray(certificationsList) ? certificationsList.find((cert) => cert.id_certificate === selectedCertificationId) : undefined,
  [certificationsList, selectedCertificationId]
 );

 const renderQuestionnaireContent = useMemo(() => {
  if (!selectedQuestionnaire) return null;

  const commonProps = {
    companyId: selectedCompanyId,
    surveyId: selectedQuestionnaire.s_id_survey,
    companyGroupId: companyData?.idCompanyGroup,
    formName: selectedQuestionnaire.s_description,
    selectedCompanyId,
    isResponsibleUser: false,
    openChat,
  };

  switch (selectedQuestionnaire.s_type) {
    case 'periodic':
      return <PeriodicType {...commonProps} />;
    case 'diagnostic':
      return <DiagnosisType {...commonProps} />;
    case 'certification':
      return selectedQuestionnaire?.has_pillar ? (
        <PillarCertification {...commonProps} />
      ) : (
        <SimpleCertification {...commonProps} />
      );
    default:
      return <Questionnaire {...commonProps} />;
  }
}, [selectedQuestionnaire, selectedCompanyId, companyData, openChat]);

 if (companyLoading) {
  return <Loader />;
 }

 if (companyError || formsError) {
  return <Text>Failed to load data.</Text>;
 }

 return (
  <MainContent large minH='90vh'>
   <Breadcrumb spacing='8px' mb='24px' separator={<IoChevronForwardSharp />}>
    <BreadcrumbItem>
     <BreadcrumbLink as={Link} to='/registration' textDecor='underline'>
      Empresas
     </BreadcrumbLink>
    </BreadcrumbItem>

    <BreadcrumbItem>
     <BreadcrumbLink href='#' isCurrentPage fontWeight={700}>
      Questionários & Certificações
     </BreadcrumbLink>
    </BreadcrumbItem>
   </Breadcrumb>

   <CompanyHeader
    idCompany={idCompany}
    companies={companyData?.companies}
    selectedCompanyId={selectedCompanyId}
    setSelectedCompanyId={setSelectedCompanyId}
   />

   <Tabs size='md' variant='primary' mt='30px' onChange={(index) => setActiveTab(index)}>
    <Box p='24px 32px' bg='white' borderRadius='12px' mb='6'>
     <TabList mb='21.5px'>
      <Tab>Questionários</Tab>
      <Tab>Certificações</Tab>
     </TabList>

     {activeTab === 0 && (
      <Flex gap='3' align='center' wrap='nowrap' maxW='calc(100% - 380px)'>
       {questionnaireOptions?.length > 0 && (
        <Select
         w='250px'
         defaultValue={selectedQuestionnaireId || ''}
         value={selectedQuestionnaireId || ''}
         onChange={handleQuestionnaireChange}
         options={questionnaireOptions}
        />
       )}
       <Select
        w='150px'
        value={filterExercise}
        onChange={(e) => {
         setFilterExercise(e.target.value);
         setSelectedQuestionnaireId(null);
        }}
        options={[
         { value: '', label: 'Todos os anos' },
         ...availableExercises.map((year) => ({ value: String(year), label: String(year) })),
        ]}
       />
       <Select
        w='130px'
        ml='-8'
        value={filterStatus}
        onChange={(e) => {
         setFilterStatus(e.target.value);
         setSelectedQuestionnaireId(null);
        }}
        options={[
         { value: '', label: 'Todos' },
         { value: 'true', label: 'Ativo' },
         { value: 'false', label: 'Inativo' },
        ]}
       />
      </Flex>
     )}

     {activeTab === 1 && (
      <Flex gap='4' justify='space-between'>
       <Flex gap='3' align='center' wrap='nowrap'>
        {certificationOptions?.length > 0 && selectedCertificationId && (
         <Select
          w='250px'
          defaultValue={selectedCertificationId || ''}
          value={selectedCertificationId || ''}
          onChange={handleCertificationChange}
          options={certificationOptions}
         />
        )}
        <Select
         w='180px'
         value={filterCertExercise}
         onChange={(e) => {
          setFilterCertExercise(e.target.value);
          setSelectedCertificationId(null);
         }}
         options={[
          { value: '', label: 'Todos os anos' },
          ...availableCertExercises.map((year) => ({ value: String(year), label: String(year) })),
         ]}
        />
        <Select
         w='130px'
         value={filterCertStatus}
         onChange={(e) => {
          setFilterCertStatus(e.target.value);
          setSelectedCertificationId(null);
         }}
         options={[
          { value: '', label: 'Todos' },
          { value: 'true', label: 'Ativo' },
          { value: 'false', label: 'Inativo' },
         ]}
        />
       </Flex>
      </Flex>
     )}
    </Box>
    <TabPanels>
     <TabPanel p={0}>
      {!Array.isArray(formsList) || formsList?.length === 0 ? (
       <Flex p='52px 32px'>
        <Text fontSize='15px' fontWeight={500}>
         Não existem questionários vinculados a essa empresa.
        </Text>
       </Flex>
      ) : (
       <Box>{renderQuestionnaireContent}</Box>
      )}
     </TabPanel>

     <TabPanel p={0}>
      {!Array.isArray(certificationsList) || certificationsList?.length === 0 ? (
       <Flex p='52px 32px'>
        <Text fontSize='15px' fontWeight={500}>
         Não existem certificações vinculadas a essa empresa.
        </Text>
       </Flex>
      ) : (
       <Box>
        <CertificationContent
         selectedCertificationId={selectedCertificationId}
         hasPillars={selectedCertification?.has_pillar}
         selectedCompanyId={selectedCompanyId}
         surveyId={selectedCertificationId}
         companyGroupId={companyData?.idCompanyGroup}
         companyName={companyData?.companies?.[0].c_name || ''}
         idLinkGroup={selectedCertification?.id_link}
         linkStatus={selectedCertification?.status}
        />
       </Box>
      )}
     </TabPanel>
    </TabPanels>
   </Tabs>
  </MainContent>
 );
}
